import {
  type DashboardLayoutItem,
  type DashboardLayoutState,
  type WidgetInstance,
  DASHBOARD_LAYOUT_VERSION,
} from '../types';
import { isKnownWidgetId } from '../widgetRegistry';

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Coerces an untrusted persisted blob (from `TableView.customFields` JSON or
 * localStorage) into a safe DashboardLayoutState, falling back to `seed` on any
 * structural problem. This is the only guardrail against a corrupt/legacy blob
 * crashing the landing page, so it must never throw.
 *
 * - Wrong shape / wrong version → return `seed`.
 * - Drops widget instances whose id is no longer in the registry (+ their layout items).
 * - Drops layout items that don't reference a surviving instance.
 */
export function validateAndMigrate(raw: unknown, seed: DashboardLayoutState): DashboardLayoutState {
  try {
    if (!raw || typeof raw !== 'object') {
      return seed;
    }
    const candidate = raw as Partial<DashboardLayoutState>;

    // Persisted layouts carry a `version` so future breaking shape changes can be
    // migrated forward. We deliberately do NOT discard the whole layout on a
    // version mismatch — that would silently wipe a user's customized dashboard the
    // first time the version is bumped. Instead we always run the structural
    // sanitiser below, which drops anything it can't understand (unknown widget
    // ids, orphaned or non-finite layout items) and keeps the rest. When a real
    // breaking change lands, transform `candidate` for the older version here,
    // before it reaches the sanitiser.
    if (
      !Array.isArray(candidate.instances) ||
      typeof candidate.layouts !== 'object' ||
      candidate.layouts === null
    ) {
      return seed;
    }

    const seenInstanceIds = new Set<string>();
    const instances: WidgetInstance[] = candidate.instances.filter(
      (inst): inst is WidgetInstance => {
        if (
          !inst ||
          typeof inst !== 'object' ||
          typeof (inst as WidgetInstance).instanceId !== 'string' ||
          !isKnownWidgetId((inst as WidgetInstance).widgetId)
        ) {
          return false;
        }
        // Drop duplicate instanceIds — they would become duplicate React/grid keys.
        const id = (inst as WidgetInstance).instanceId;
        if (seenInstanceIds.has(id)) {
          return false;
        }
        seenInstanceIds.add(id);
        return true;
      }
    );

    const validInstanceIds = new Set(instances.map(inst => inst.instanceId));

    const layouts: Record<string, DashboardLayoutItem[]> = {};
    for (const [breakpoint, items] of Object.entries(candidate.layouts)) {
      if (!Array.isArray(items)) {
        continue;
      }
      const seenLayoutKeys = new Set<string>();
      const breakpointItems: DashboardLayoutItem[] = [];
      for (const item of items as DashboardLayoutItem[]) {
        if (
          !item ||
          typeof item.i !== 'string' ||
          !validInstanceIds.has(item.i) ||
          // Drop duplicate layout keys for a breakpoint — they would become duplicate keys.
          seenLayoutKeys.has(item.i) ||
          // Reject non-finite geometry so corrupt blobs can't crash react-grid-layout.
          !isFiniteNumber(item.x) ||
          !isFiniteNumber(item.y) ||
          !isFiniteNumber(item.w) ||
          !isFiniteNumber(item.h)
        ) {
          continue;
        }
        seenLayoutKeys.add(item.i);
        breakpointItems.push({
          ...item,
          x: Math.max(0, Math.floor(item.x)),
          y: Math.max(0, Math.floor(item.y)),
          w: Math.max(1, Math.floor(item.w)),
          h: Math.max(1, Math.floor(item.h)),
        });
      }
      layouts[breakpoint] = breakpointItems;
    }

    return { version: DASHBOARD_LAYOUT_VERSION, instances, layouts };
  } catch {
    return seed;
  }
}
